import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { DocumentState } from "@/services/backend/documents/types/state";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import {
  Contract,
  ContractState,
  Patch,
} from "@/services/backend/omni/contract/types/contract";
import { PositionIdPatch } from "@/services/backend/omni/position/types/position";
import { ScopeOfWorkPatch } from "@/services/backend/omni/scope-of-work/types/scope-of-work";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { AddPositionBaseRequest } from "../../position/types/position-requests";
import { Grouping } from "@/services/backend/omni/position/types/grouping";

const invalidatesContract = (
  _result: unknown,
  _error: unknown,
  request: { id: string },
) => [
  { type: "OmniContract" as const, id: request.id },
  { type: "OmniContract" as const, id: "LIST" },
];

const contractApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    omniContractCreate: build.mutation<Contract, CreateRequest>({
      query: (request) => ({
        url: `/omni/contract`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractShow: build.query<Contract, ShowRequest>({
      query: (request) => ({
        url: `/omni/contract`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("OmniContract"),
    }),
    omniContractList: build.query<Lst<Contract>, ListRequest>({
      query: (request) => ({
        url: `/omni/contract/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "OmniContract"),
    }),
    omniContractPatch: build.mutation<Contract, PatchRequest>({
      query: (request) => ({
        url: `/omni/contract`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractCreateScopeOfWork: build.mutation<
      Contract,
      CreateScopeOfWorkRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractPatchScopeOfWork: build.mutation<
      Contract,
      PatchScopeOfWorkRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractRemoveScopeOfWork: build.mutation<
      Contract,
      RemoveScopeOfWorkRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractAddScopeOfWorkPosition: build.mutation<
      Contract,
      AddScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractPatchScopeOfWorkPosition: build.mutation<
      Contract,
      PatchScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/position`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractPatchScopeOfWorkPositions: build.mutation<
      Contract,
      PatchScopeOfWorkPositionsRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/positions`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractRemoveScopeOfWorkPosition: build.mutation<
      Contract,
      RemoveScopeOfWorkPositionRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractRemoveScopeOfWorkPositionsByGrouping: build.mutation<
      Contract,
      RemoveScopeOfWorkPositionsByGroupingRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/positions/grouping`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractRenameScopeOfWorkGrouping: build.mutation<
      Contract,
      RenameScopeOfWorkGroupingRequest
    >({
      query: (request) => ({
        url: `/omni/contract/scope-of-work/positions/grouping`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractResetReferenceNumber: build.mutation<
      Contract,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/omni/contract/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractUpdateState: build.mutation<Contract, UpdateStateRequest>({
      query: (request) => ({
        url: `/omni/contract/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesContract,
    }),
    omniContractDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/omni/contract`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["OmniContract"],
    }),
    omniContractPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/omni/contract`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    omniContractPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/omni/contract`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
      invalidatesTags: invalidatesContract,
    }),
  }),
});

export const {
  useOmniContractCreateMutation,
  useOmniContractShowQuery,
  useOmniContractListQuery,
  useOmniContractPatchMutation,
  useOmniContractCreateScopeOfWorkMutation,
  useOmniContractPatchScopeOfWorkMutation,
  useOmniContractRemoveScopeOfWorkMutation,
  useOmniContractAddScopeOfWorkPositionMutation,
  useOmniContractPatchScopeOfWorkPositionMutation,
  useOmniContractPatchScopeOfWorkPositionsMutation,
  useOmniContractRemoveScopeOfWorkPositionMutation,
  useOmniContractRemoveScopeOfWorkPositionsByGroupingMutation,
  useOmniContractRenameScopeOfWorkGroupingMutation,
  useOmniContractResetReferenceNumberMutation,
  useOmniContractUpdateStateMutation,
  useOmniContractDeleteMutation,
  useLazyOmniContractPdfPreviewQuery,
  useOmniContractPdfDownloadMutation,
} = contractApi;

export const updateOmniContractCache = updateCache(
  contractApi,
  "omniContractShow",
);

/** Mirrors `contract.CreateRequest`, backend/core/omni/domain/contract/service_port.go. */
export type CreateRequest = {
  id: string;
  serviceKind: GSMServiceKind;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  offerId?: string;
  baseDataId?: string;
  serviceVariation?: ServiceVariation;
};

/** Mirrors `contract.ShowRequest`, backend/core/omni/domain/contract/service_port.go. */
export type ShowRequest = {
  id: string;
};

/** Mirrors `contract.ListRequest`, backend/core/omni/domain/contract/service_port.go. */
export type ListRequest = DocumentBaseFilters &
  DocumentCommonFilters &
  ReferenceNumberFilters & {
    orderBy?: OrdrBy<ListOrderBy>[];
    serviceKind?: Filter<GSMServiceKind>;
    state?: Filter<DocumentState>;
    contractState?: Filter<ContractState>;
    offerId?: Filter<string>;
    limit?: number;
    offset?: number;
  };

export type ListOrderBy =
  | "id"
  | "createdAt"
  | "updatedAt"
  | "startDate"
  | "referenceNumber";

/** Mirrors `contract.PatchRequest`, backend/core/omni/domain/contract/service_port.go. */
export type PatchRequest = Patch & {
  id: string;
};

/** Mirrors `contract.CreateScopeOfWorkRequest`, backend/core/omni/domain/contract/service_port.go. */
export type CreateScopeOfWorkRequest = {
  id: string;
  baseDataId?: string;
  scopeOfWorkId: string;
  serviceObjectId?: string;
  serviceVariation?: ServiceVariation;
  title?: string;
  subtitle?: string;
  text?: string;
};

/** Mirrors `contract.PatchScopeOfWorkRequest`, backend/core/omni/domain/contract/service_port.go. */
export type PatchScopeOfWorkRequest = ScopeOfWorkPatch & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `contract.RemoveScopeOfWorkRequest`, backend/core/omni/domain/contract/service_port.go. */
export type RemoveScopeOfWorkRequest = {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `contract.AddScopeOfWorkPositionRequest`, backend/core/omni/domain/contract/service_port.go. */
export type AddScopeOfWorkPositionRequest = AddPositionBaseRequest & {
  scopeOfWorkId: string;
};

/** Mirrors `contract.PatchScopeOfWorkPositionRequest`, backend/core/omni/domain/contract/service_port.go. */
export type PatchScopeOfWorkPositionRequest = PositionIdPatch & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `contract.PatchScopeOfWorkPositionsRequest`, backend/core/omni/domain/contract/service_port.go. */
export type PatchScopeOfWorkPositionsRequest = {
  id: string;
  scopeOfWorkId: string;
  positions: PositionIdPatch[];
};

/** Mirrors `contract.RemoveScopeOfWorkPositionRequest`, backend/core/omni/domain/contract/service_port.go. */
export type RemoveScopeOfWorkPositionRequest = {
  id: string;
  scopeOfWorkId: string;
  positionId: string;
};

/** Mirrors `contract.RemoveScopeOfWorkPositionsByGroupingRequest`, backend/core/omni/domain/contract/service_port.go. */
export type RemoveScopeOfWorkPositionsByGroupingRequest = Grouping & {
  id: string;
  scopeOfWorkId: string;
};

/** Mirrors `contract.RenameScopeOfWorkGroupingRequest`, backend/core/omni/domain/contract/service_port.go. */
export type RenameScopeOfWorkGroupingRequest = Grouping & {
  id: string;
  scopeOfWorkId: string;
  newGroup?: string;
  newArea?: string;
};

/** Mirrors `contract.UpdateStateRequest`, backend/core/omni/domain/contract/service_port.go. */
export type UpdateStateRequest = {
  id: string;
  state: DocumentState;
  stateSetBy: string;
};

/** Mirrors `contract.DeleteRequest`, backend/core/omni/domain/contract/service_port.go. */
export type DeleteRequest = {
  id: string;
};

/** Mirrors `mjolnirroutes.ShowContractRequest`, backend/core/omni/adapters/driving/mjolnirroutes/contract_routes.go. */
export type PDFRequest = {
  id: string;
  document?:
    | ""
    | "contract"
    | "scope-of-work"
    | "pricing-by-area"
    | "data-sheets";
};
